import { Box, darken, List, ListItem, Typography } from '@mui/material';
import { LabeledValue } from '~/components/Fields/LabeledValue';
import ReviewStatus from '~/components/Structures/ReviewStatus';
import { Roi } from '~/domain/dicom/dicomstructureset';
import { StructureSet } from '~/domain/patient/structureset';

type StructureSetConfirmationProps = {
    selectedStructureSets: StructureSet[];
    structureSelections: Record<string, Roi[]>;
};

export default function StructureSetConfirmation({
    selectedStructureSets,
    structureSelections,
}: StructureSetConfirmationProps) {
    const structureSets = selectedStructureSets.map((ss) => {
        const selectedRois =
            structureSelections[ss.dicomStructureSet.sopInstanceUID] || [];
        const unapprovedRois = selectedRois.filter((roi) => !roi.isApproved);
        return {
            structureSet: ss,
            unapprovedRois,
            selectedRois,
        };
    });

    return (
        <Box display="grid" gap={2}>
            <Box display="grid" gap={1}>
                <Typography fontSize={14}>
                    The following must be acknowledged when exporting structure
                    set:
                </Typography>
                <List sx={{ pl: 2, listStyleType: 'disc', fontSize: 13 }}>
                    <ListItem sx={{ display: 'list-item', py: 0.2 }}>
                        DICOM only implements approval status (300E,0002) at
                        structure set level.
                    </ListItem>
                    <ListItem sx={{ display: 'list-item', py: 0.2 }}>
                        Individual structure approval status is not exported.
                    </ListItem>
                    <ListItem sx={{ display: 'list-item', py: 0.2 }}>
                        Selected datasets will be exported through DICOM. This
                        operation cannot be reverted.
                    </ListItem>
                </List>
            </Box>
            <Box display="grid" gap={1}>
                {structureSets.map(({ structureSet, selectedRois }) => (
                    <Box
                        key={structureSet.dicomUid}
                        display="grid"
                        px={2}
                        py={1}
                        gap={1}
                        bgcolor={(theme) =>
                            darken(theme.palette.background.paper, 0.1)
                        }
                        borderRadius={1}
                        border="1px solid"
                        borderColor={(theme) =>
                            darken(theme.palette.background.paper, 0.4)
                        }
                    >
                        <Box display="grid" gridTemplateColumns="1fr 1fr 1fr">
                            <LabeledValue
                                label="Name"
                                value={structureSet.structureSetLabel}
                            />
                            <LabeledValue
                                label="Selected structures"
                                value={
                                    <Typography
                                        fontSize={13}
                                        lineHeight={1}
                                        display="flex"
                                        alignItems="center"
                                        gap={1}
                                    >
                                        {selectedRois.length}
                                    </Typography>
                                }
                            />
                            <LabeledValue
                                label="Approval status"
                                value={
                                    <ReviewStatus
                                        status={structureSet.approvalStatus}
                                    />
                                }
                            />
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}
